import { ApiClient } from './apiClient';
import {
  CopyOrderRequest,
  CopyOrderResponse,
  GenerateRandomOrderRequest,
  GenerateRandomOrderResponse,
  CreateOrderWithLinesRequest,
  CreateOrderWithLinesResponse,
  AvailableItemForOrder,
  Warehouse,
  ValidatePickListNoERPResponse,
  GeneratePickListNoERPResponse,
  NextOrderNoResponse
} from '../types/orderRegistration';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class OrderRegistrationService {
  /**
   * Copy an existing order
   */
  async copyOrder(orderNo: number): Promise<CopyOrderResponse> {
    const request: CopyOrderRequest = { sourceOrderNo: orderNo };
    const response = await ApiClient.post(`${API_BASE_URL}/orders/${orderNo}/copy`, request);

    if (!response.ok) {
      throw new Error(`Failed to copy order: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Generate a random test order
   */
  async generateRandomOrder(request: GenerateRandomOrderRequest): Promise<GenerateRandomOrderResponse> {
    const response = await ApiClient.post(`${API_BASE_URL}/orders/generate-random`, request);

    if (!response.ok) {
      throw new Error(`Failed to generate random order: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create an order with lines
   */
  async createOrderWithLines(request: CreateOrderWithLinesRequest): Promise<CreateOrderWithLinesResponse> {
    const response = await ApiClient.post(`${API_BASE_URL}/orders/with-lines`, request);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to create order: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get available items for order creation
   */
  async getAvailableItems(requireStock: boolean = false): Promise<AvailableItemForOrder[]> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/available-items?requireStock=${requireStock}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch available items: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get list of warehouses
   */
  async getWarehouses(): Promise<Warehouse[]> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/warehouses`);

    if (!response.ok) {
      throw new Error(`Failed to fetch warehouses: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Validate PickListNoERP
   */
  async validatePickListNoERP(pickListNoERP: string): Promise<ValidatePickListNoERPResponse> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/validate-picklistno/${encodeURIComponent(pickListNoERP)}`);

    if (!response.ok) {
      throw new Error(`Failed to validate PickListNoERP: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get next available OrderNo
   */
  async getNextOrderNo(): Promise<number> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/next-orderno`);

    if (!response.ok) {
      throw new Error(`Failed to get next OrderNo: ${response.statusText}`);
    }

    const data: NextOrderNoResponse = await response.json();
    return data.nextOrderNo;
  }

  /**
   * Generate unique PickListNoERP
   */
  async generatePickListNoERP(): Promise<string> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/generate-picklistno`);

    if (!response.ok) {
      throw new Error(`Failed to generate PickListNoERP: ${response.statusText}`);
    }

    const data: GeneratePickListNoERPResponse = await response.json();
    return data.pickListNoERP;
  }

  /**
   * Format date for display
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('no-NO');
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format quantity for display
   */
  formatQuantity(quantity?: number): string {
    if (quantity === undefined || quantity === null) return '';
    return new Intl.NumberFormat('no-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(quantity);
  }
}

export const orderRegistrationService = new OrderRegistrationService();
